import { Tabs, TabItem } from '@restworld/ui-ds-base';
import { JobApplicationStatus, JobApplicationTabContent } from './job-application-tab-content';
import { atom, useAtomValue } from 'jotai';
import { JobApplication } from '@restworld/utility-types';
import {
  inEvaluationJobApplicationsAtom,
  activeJobApplicationsAtom,
  pastJobApplicationsAtom,
  inEvaluationJobApplicationsLoadingAtom,
  activeJobApplicationsLoadingAtom,
  pastJobApplicationsLoadingAtom,
  inEvaluationJobApplicationsHasMoreAtom,
  activeJobApplicationsHasMoreAtom,
  pastJobApplicationsHasMoreAtom,
} from '../global-state';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { getSentimentDescription } from '../application.utils';
import { useJobApplicationCategoriesNavigation } from '../hooks/use-job-application-categories-navigation';
import { useTranslation } from 'next-i18next';
import type { TFunction } from 'next-i18next';
import { useLoadWorkerApplications } from '../hooks/use-load-worker-applications';
import { cn } from '@restworld/utils-common';
import { getPreInterview } from '../pre-interview';

// Transform job application data for UI display
const transformJobApplicationForUI = (jobApplication: JobApplication) => {
  const restaurantId = jobApplication?.restaurant_id;

  return {
    restaurantName: restaurantId
      ? jobApplication?.restaurant?.restaurant_name
      : jobApplication?.job_position?.restaurant?.restaurant_name,
    city: restaurantId ? jobApplication?.restaurant?.locality : jobApplication?.job_position?.restaurant?.locality,
    jobTitle: jobApplication?.job_position?.job_title,
    occupation: jobApplication?.job_position?.occupation?.label_it?.label,
    salaryMin: jobApplication?.job_position?.min_salary,
    salaryMax: jobApplication?.job_position?.max_salary,
    jobType: jobApplication?.job_position?.job_type_var?.label,
    publishedAt: jobApplication?.inserted_at,
    redirectUrl: `/candidature/${jobApplication?.restaurant_id || jobApplication?.job_position_id}`,
    isLoading: false,
    id: jobApplication?.job_position?.id,
    sentiment: jobApplication?.status?.sentiment,
    sentimentLabel: jobApplication?.status?.label,
    restaurantId,
    statusKey: jobApplication?.status_key,
    discardedBy: jobApplication?.discarded_by,
    showRestaurantDetails: jobApplication?.job_position?.show_restaurant_details,
    // Raw data only. The rejection sentence is user-facing copy, so it is
    // composed in the component below, which has `t`; these atoms cannot.
    isDiscarded: jobApplication?.status_key === 'worker_jp_discarded',
    // Same precedence as the detail timeline: the "other reason" flow writes no
    // variable at all, so without this fallback those rejections showed the
    // label with no reason behind it.
    discardedReason: jobApplication?.discarded_reason_var?.label || jobApplication?.discarded_other_reason,
  };
};

/** Adds the translated rejection sentence to a transformed application list. */
const useApplicationsWithSentimentDescription = (
  applications:
    | (ReturnType<typeof transformJobApplicationForUI> & {
        preInterview?: ReturnType<typeof getPreInterview>;
      })[]
    | undefined,
  t: TFunction
) =>
  useMemo(
    () =>
      applications?.map(({ isDiscarded, discardedReason, preInterview, ...application }) => ({
        ...application,
        ...(isDiscarded && {
          sentimentDescription: getSentimentDescription({
            discardedBy: application.discardedBy,
            discardedReason,
            t,
          }),
        }),
        ...(preInterview && { preInterview }),
      })),
    [applications, t]
  );

// Atoms for transformed application data
const jobApplicationsInEvaluationUIAtom = atom((get) => {
  const applications = get(inEvaluationJobApplicationsAtom);
  return applications?.map((application) => ({
    ...transformJobApplicationForUI(application),
    preInterview: getPreInterview(application),
  }));
});

export const jobApplicationWithPositiveSentimentAtom = atom((get) => {
  const applications = get(activeJobApplicationsAtom);
  return applications?.map(transformJobApplicationForUI);
});

const jobApplicationsWithNegativeSentimentUIAtom = atom((get) => {
  const applications = get(pastJobApplicationsAtom);
  return applications?.map(transformJobApplicationForUI);
});

export const WorkerJobApplications = () => {
  const [defaultTab, setDefaultTab] = useState<number | number>(undefined);
  const router = useRouter();
  const { jobApplicationCategories } = useJobApplicationCategoriesNavigation();
  const { query } = router;
  const category = query?.category as string;
  const { t } = useTranslation();
  const { fetchMoreCategoryApplications } = useLoadWorkerApplications({ showLoader: false });

  // Use the new categorized atoms
  const jobApplicationsInEvaluation = useApplicationsWithSentimentDescription(
    useAtomValue(jobApplicationsInEvaluationUIAtom),
    t
  );
  const jobApplicationWithPositiveSentiment = useApplicationsWithSentimentDescription(
    useAtomValue(jobApplicationWithPositiveSentimentAtom),
    t
  );
  const jobApplicationsWithNegativeSentiment = useApplicationsWithSentimentDescription(
    useAtomValue(jobApplicationsWithNegativeSentimentUIAtom),
    t
  );

  // Loading states
  const inEvaluationLoading = useAtomValue(inEvaluationJobApplicationsLoadingAtom);
  const activeLoading = useAtomValue(activeJobApplicationsLoadingAtom);
  const pastLoading = useAtomValue(pastJobApplicationsLoadingAtom);

  // Has more states
  const inEvaluationHasMore = useAtomValue(inEvaluationJobApplicationsHasMoreAtom);
  const activeHasMore = useAtomValue(activeJobApplicationsHasMoreAtom);
  const pastHasMore = useAtomValue(pastJobApplicationsHasMoreAtom);

  useEffect(() => {
    if (category) {
      const index = jobApplicationCategories.findIndex((jobCategory) => jobCategory === category);
      setDefaultTab(index);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const evaluatedJobApplications = useMemo(
    () => (
      <JobApplicationTabContent
        title={t('job-application-page:being_review_text')}
        status={JobApplicationStatus.IN_EVALUATION}
        applications={jobApplicationsInEvaluation}
        onLoadMore={() => fetchMoreCategoryApplications('in_evaluation')}
        hasMore={inEvaluationHasMore}
        loading={inEvaluationLoading}
      />
    ),
    [t, jobApplicationsInEvaluation, fetchMoreCategoryApplications, inEvaluationHasMore, inEvaluationLoading]
  );

  const positiveJobApplications = useMemo(
    () => (
      <JobApplicationTabContent
        title={t('job-application-page:sent_application')}
        status={JobApplicationStatus.POSITIVE}
        applications={jobApplicationWithPositiveSentiment}
        onLoadMore={() => fetchMoreCategoryApplications('active')}
        hasMore={activeHasMore}
        loading={activeLoading}
      />
    ),
    [t, jobApplicationWithPositiveSentiment, fetchMoreCategoryApplications, activeHasMore, activeLoading]
  );

  const negativeJobApplications = useMemo(
    () => (
      <JobApplicationTabContent
        title={t('job-application-page:history_of_all_the_job_positions')}
        status={JobApplicationStatus.NEGATIVE}
        applications={jobApplicationsWithNegativeSentiment}
        onLoadMore={() => fetchMoreCategoryApplications('past')}
        hasMore={pastHasMore}
        loading={pastLoading}
      />
    ),
    [t, jobApplicationsWithNegativeSentiment, fetchMoreCategoryApplications, pastHasMore, pastLoading]
  );

  const tabs = useMemo<TabItem[]>(
    () => [
      {
        label: t('job-application-page:under_evaluation'),
        content: evaluatedJobApplications,
        badge: jobApplicationsInEvaluation?.length > 0 ? jobApplicationsInEvaluation.length : undefined,
      },
      {
        label: t('common:active'),
        content: positiveJobApplications,
        badge: jobApplicationWithPositiveSentiment?.length > 0 ? jobApplicationWithPositiveSentiment.length : undefined,
      },
      {
        label: t('common:history'),
        content: negativeJobApplications,
      },
    ],
    [
      t,
      evaluatedJobApplications,
      jobApplicationsInEvaluation?.length,
      positiveJobApplications,
      jobApplicationWithPositiveSentiment?.length,
      negativeJobApplications,
    ]
  );

  const onHandleTabChange = useCallback(
    (index?: number) => {
      if (index === undefined) {
        router.push({ query: {}, pathname: router.pathname }, undefined, { shallow: true });
      } else {
        const updatedQuery = { ...query, category: jobApplicationCategories[index] as string };
        setDefaultTab(index);
        router.push({ query: updatedQuery, pathname: router.pathname }, undefined, { shallow: true });
      }
    },
    [query, router, jobApplicationCategories]
  );

  // The same tabs on every width. On mobile the legacy `MobileTabs` was a list of three
  // rows, each opening a full-screen overlay: the applications were two taps away and
  // never on the first screen.
  return (
    <WorkerJobApplicationsWrapper>
      <Tabs tabs={tabs} defaultIndex={defaultTab} onTabChange={onHandleTabChange} tabListClassName="pt-0" />
    </WorkerJobApplicationsWrapper>
  );
};

const WorkerJobApplicationsWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex items-start flex-col w-full', className)} {...props} />
);
